import { useMutation, useQueryClient } from '@tanstack/react-query';
import { put } from '@/shared/api/api-client';
import { invalidateIfNotInFlight } from '@/patients/lib/invalidate-if-not-in-flight';
import type { UpdateConditionBody } from '@/types/patient-chart-api';

/**
 * PUT /api/Patients/condition/{id}
 *
 * Single endpoint for Edit, Resolve, AND soft-Delete on Conditions —
 * the body shape is identical; the action varies only by
 * `clinicalStatus` / `endDate` / `endDisplayDate` / `isDeleted`. Wire
 * body curl-verified by Andrew 2026-05-19 (three curls captured —
 * Edit/Resolve/Delete — all confirmed identical shape modulo those
 * four fields).
 *
 * Round-trip rules vs Allergies' PUT:
 * - `clinicalStatus` flips to `"Resolved"` on the Resolve flow, with
 *   `endDate` + `endDisplayDate` populated.
 * - Top-level `speciality` IS populated from the original record
 *   (Allergies shipped null here — Conditions doesn't).
 * - `performerDetails` round-trips the full `PerformerDetailsFull`
 *   shape from the original record.
 * - `asserter`, `createdDate` preserve original values.
 *
 * Cache: invalidates `['patient-conditions', patientId]` (prefix match
 * — covers both Active and Resolved variants so the Resolve flow
 * moves the row across sections without a refetch race).
 */

interface UpdateConditionParams {
  patientId: string;
  conditionId: string;
  body: UpdateConditionBody;
}

export function useUpdateCondition() {
  const qc = useQueryClient();
  return useMutation<string, Error, UpdateConditionParams>({
    mutationFn: ({ conditionId, body }) =>
      put<string>(`/api/Patients/condition/${conditionId}`, body),
    onSuccess: (_data, { patientId }) => {
      invalidateIfNotInFlight(qc, ['patient-conditions', patientId]);
    },
  });
}
